"use client";

import { useRef, useState } from "react";
import {
  arrayUnion,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "@/lib/firebase";
import { useTranslations } from "@/contexts/I18nContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import {
  buildPetProfileDoc,
  PET_SPECIES_OPTIONS,
  type PetSpecies,
} from "@/lib/createPet";

/**
 * Wave N.3 — "Add Pet" modal. Mirrors iOS `AddPetView.swift` create flow.
 * Renders only when `isOpen` is true; renders nothing otherwise so it can
 * be unconditionally mounted at the page level.
 *
 * The modal builds the Firestore body via the pure helper
 * `buildPetProfileDoc` so the input validation and field-shaping rules can
 * be unit-tested without mounting the component (see `createPet.test.ts`).
 *
 * Storage notes — pet avatars are uploaded to
 * `member_profiles/{petUid}/avatar.jpg`. If the upload fails (e.g. because
 * Storage rules haven't been extended for this path yet), the pet is
 * still created without `profile_url` and the user sees a non-blocking
 * warning. This mirrors the iOS resilience pattern where avatar failures
 * never abort the save.
 */

interface AddPetModalProps {
  isOpen: boolean;
  onClose: () => void;
  householdId: string;
  createdBy: string;
  onError?: (message: string) => void;
}

const SPECIES_LABEL_KEYS: Record<PetSpecies, string> = {
  dog: "pets.species.dog",
  cat: "pets.species.cat",
  bird: "pets.species.bird",
  rabbit: "pets.species.rabbit",
  fish: "pets.species.fish",
  reptile: "pets.species.reptile",
  other: "pets.species.other",
};

export function AddPetModal({
  isOpen,
  onClose,
  householdId,
  createdBy,
  onError,
}: AddPetModalProps) {
  const t = useTranslations();
  const { refreshHousehold } = useHouseholdContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [species, setSpecies] = useState<PetSpecies>("dog");
  const [breed, setBreed] = useState("");
  // Birthday split into month/day/(optional)year to match iOS
  // `AddPetView.swift:42-54` and the human `MemberEditModal` block.
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function resetAndClose() {
    setName("");
    setSpecies("dog");
    setBreed("");
    setBirthMonth("");
    setBirthDay("");
    setBirthYear("");
    setPhotoFile(null);
    setPhotoPreview(null);
    setSubmitting(false);
    onClose();
  }

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }
    setPhotoFile(file);
    // Best-effort local preview. Will be revoked when the modal closes
    // via the next reset cycle (browser GCs object URLs on page unload).
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function uploadPetAvatar(petUid: string, file: File): Promise<string | null> {
    try {
      const { compressForAvatar } = await import("@/lib/imageCompressor");
      const compressed = await compressForAvatar(file);
      const storageRef = ref(storage, `member_profiles/${petUid}/avatar.jpg`);
      await uploadBytes(storageRef, compressed);
      return await getDownloadURL(storageRef);
    } catch (err) {
      console.error("Failed to upload pet avatar:", err);
      return null;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    if (!name.trim() || !householdId) {
      return;
    }

    setSubmitting(true);

    // Generate the pet UID up front so we can wire the Storage path,
    // the member_profiles doc, and the household_members array all to
    // the same ID. `crypto.randomUUID()` is widely supported in modern
    // browsers; we fall back to a manual UUID for older runtimes.
    const petUid = generatePetUid();

    let profileUrl: string | undefined;
    if (photoFile) {
      const url = await uploadPetAvatar(petUid, photoFile);
      if (url) {
        profileUrl = url;
      } else {
        onError?.(t("pets.add.photoFailed"));
        // Continue without photo — pet creation should not be blocked
        // by a Storage rule failure or transient upload error.
      }
    }

    const docBody = buildPetProfileDoc(
      {
        name,
        species,
        breed,
        birthMonth: parseIntOrNull(birthMonth),
        birthDay: parseIntOrNull(birthDay),
        birthYear: parseIntOrNull(birthYear),
        profileUrl,
      },
      householdId,
      petUid,
      createdBy || undefined,
    );

    if (!docBody) {
      setSubmitting(false);
      return;
    }

    try {
      await setDoc(doc(db, "member_profiles", petUid), docBody);
      await updateDoc(doc(db, "households", householdId), {
        household_members: arrayUnion(petUid),
      });
      // Belt-and-suspenders: kick a synchronous refresh so the new pet shows
      // immediately even if the snapshot listener delivery is delayed.
      await refreshHousehold();
      resetAndClose();
    } catch (err) {
      console.error("Failed to create pet:", err);
      onError?.(t("pets.add.error"));
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !submitting;

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!submitting) resetAndClose();
        }}
      />
      <div className="relative z-50 w-full max-w-md mx-4 mb-4 sm:mb-0 bg-white rounded-2xl shadow-xl overflow-hidden text-gray-900 max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="font-semibold text-center">{t("pets.add.title")}</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto">
          {/* Photo (optional) */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="relative w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 hover:bg-gray-200 transition-colors"
              aria-label={t("pets.fields.photo.label")}
            >
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt={t("pets.fields.photo.label")}
                  className="w-full h-full object-cover"
                />
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-400"
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
            <div className="text-xs text-gray-500">
              {t("pets.fields.photo.hint")}
            </div>
          </div>

          {/* Name (required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("pets.fields.name.label")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={60}
              placeholder={t("pets.fields.name.placeholder")}
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Species (pill picker) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("pets.fields.species.label")}
            </label>
            <div
              className="flex flex-wrap gap-2"
              role="radiogroup"
              aria-label={t("pets.fields.species.label")}
            >
              {PET_SPECIES_OPTIONS.map((opt) => {
                const active = species === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSpecies(opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      active
                        ? "text-white border-transparent"
                        : "text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                    style={
                      active
                        ? { backgroundColor: "var(--accent-color, #00C7BE)" }
                        : undefined
                    }
                  >
                    {t(SPECIES_LABEL_KEYS[opt])}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Breed (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("pets.fields.breed.label")}
            </label>
            <input
              type="text"
              value={breed}
              onChange={(e) => setBreed(e.target.value)}
              maxLength={60}
              placeholder={t("pets.fields.breed.placeholder")}
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Birthday (optional) — month / day / (optional) year split,
              mirroring iOS `AddPetView.swift:42-54` and the human-member
              `MemberEditModal` block. Year is optional for privacy. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("pets.fields.birthday.label")}
            </label>
            <div className="grid grid-cols-3 gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max="12"
                value={birthMonth}
                onChange={(e) => setBirthMonth(e.target.value)}
                placeholder={t("members.add.birthMonthPlaceholder")}
                aria-label={t("members.add.birthMonthPlaceholder")}
                className="rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm text-center outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max="31"
                value={birthDay}
                onChange={(e) => setBirthDay(e.target.value)}
                placeholder={t("members.add.birthDayPlaceholder")}
                aria-label={t("members.add.birthDayPlaceholder")}
                className="rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm text-center outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
              <input
                type="number"
                inputMode="numeric"
                min="1900"
                max="2100"
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                placeholder={t("members.add.birthYearPlaceholder")}
                aria-label={t("members.add.birthYearPlaceholder")}
                className="rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm text-center outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
              className="flex-1 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              {t("pets.add.cancel")}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40 transition-opacity"
              style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
            >
              {submitting ? t("pets.add.submitting") : t("pets.add.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Parse a numeric form input into an integer, returning `null` for empty,
 * whitespace, or non-finite values. Mirrors the helper used by
 * `MemberEditModal` so the M/D/Y birthday block behaves identically for
 * humans and pets.
 */
function parseIntOrNull(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Generate a fresh ID for a new pet. Prefers `crypto.randomUUID()` (widely
 * available in modern browsers) and falls back to a manual UUID-v4-ish
 * string when running in older environments. Firestore document IDs only
 * need to be non-empty and unique within the collection — UUIDs satisfy
 * both with no risk of collision.
 */
function generatePetUid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older runtimes (very unlikely on a modern Next.js client,
  // but cheap to include and lets the modal work in test/SSR contexts).
  let id = "";
  const chars = "0123456789abcdef";
  for (let i = 0; i < 32; i++) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return id;
}
